import {useEffect, useState, type ReactNode} from 'react';
import {Check} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Textarea} from '@common-web/components/ui/textarea';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {toast} from '@common-web/hooks/use-toast';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {
  forcedResetIds,
  isEmptyMove,
  planMoveToStep,
  restartingIds,
} from '@common/groupPipeline/moveToStep';
import type {GroupPipelineDetail} from '@internal/pages/group-pipeline/components/pipelineDisplay';

type Choice = 'COMPLETED' | 'SKIPPED' | 'KEEP_OPEN';

export function MoveToStepDialog({
  vbaGroupId,
  pipeline,
  open,
  onOpenChange,
}: {
  vbaGroupId: string;
  pipeline: GroupPipelineDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  // Steps not listed here are marked complete.
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  // Steps below the target the user keeps as they are instead of sending back.
  const [kept, setKept] = useState<string[]>([]);

  const move = trpc.internal.groupPipeline.task.moveTo.useMutation({
    onSuccess: () => {
      onOpenChange(false);
      toast({title: 'Moved'});
      return Promise.all([
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupPipeline.boardColumn.invalidate(),
      ]);
    },
    onError: createMutationErrorHandler('Could not move the group'),
  });

  const tasks = pipeline.tasks;
  const current = pipeline.state.currentTask;
  // Like the mock, start on the step after the current one.
  const nextId = current ? tasks[tasks.findIndex((t) => t.id === current.id) + 1]?.id : undefined;

  useEffect(() => {
    if (!open) return;
    setTargetId(nextId ?? '');
    setReason('');
    setChoices({});
    setKept([]);
  }, [open, nextId]);

  const pickTarget = (id: string) => {
    setTargetId(id);
    setChoices({});
    setKept([]);
  };

  const sections = [...new Set(tasks.map((t) => t.section))];
  const target = tasks.find((t) => t.id === targetId);
  const plan = target ? planMoveToStep(tasks, target.id) : null;
  const choiceFor = (id: string): Choice => choices[id] ?? 'COMPLETED';
  const closes =
    plan?.closable
      .filter((t) => choiceFor(t.id) !== 'KEEP_OPEN')
      .map((t) => ({taskId: t.id, as: choiceFor(t.id) as Exclude<Choice, 'KEEP_OPEN'>})) ?? [];
  const chosenResets = plan?.resettable.map((t) => t.id).filter((id) => !kept.includes(id)) ?? [];
  const forced = plan ? forcedResetIds(plan, chosenResets) : [];
  const resetTaskIds = [...new Set([...chosenResets, ...forced])];
  const restarting = plan
    ? restartingIds(
        tasks,
        plan,
        closes.map((c) => c.taskId),
        resetTaskIds,
      )
    : [];
  const isEmpty = plan
    ? isEmptyMove(
        plan,
        closes.map((c) => c.taskId),
        resetTaskIds,
      )
    : true;
  // Reopening only shows when the target was closed; a not-started target just opens.
  const reopens = target && (target.status === 'COMPLETED' || target.status === 'SKIPPED');

  const toggle = (id: string, choice: Choice) =>
    setChoices((all) => ({...all, [id]: choiceFor(id) === choice ? 'COMPLETED' : choice}));
  // The step going back that a forced row waits on, for its tooltip.
  const goingBackKeys = new Set([
    plan?.targetKey,
    ...tasks.filter((t) => resetTaskIds.includes(t.id)).map((t) => t.key),
  ]);
  const waitsOn = (dependsOn: string[]) => {
    const key = dependsOn.find((k) => goingBackKeys.has(k));
    return tasks.find((t) => t.key === key)?.label ?? 'a step going back';
  };

  const toggleKept = (id: string) =>
    setKept((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='grid-cols-[minmax(0,1fr)] sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Move to step</DialogTitle>
        </DialogHeader>
        <div className='space-y-4'>
          <Section label='Step' htmlFor='move-target'>
            <Select value={targetId} onValueChange={pickTarget}>
              <SelectTrigger id='move-target' className='bg-white'>
                <SelectValue placeholder='Pick a step' />
              </SelectTrigger>
              <SelectContent>
                {sections.map((section) => (
                  <SelectGroup key={section}>
                    <SelectLabel>{section}</SelectLabel>
                    {tasks
                      .filter((t) => t.section === section)
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.label}
                          {t.id === current?.id && ' (now)'}
                        </SelectItem>
                      ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </Section>

          {plan && plan.closable.length > 0 && (
            <Section label='Marks complete'>
              <Rows>
                {plan.closable.map((t) => {
                  const required = plan.requiredIds.includes(t.id);
                  const choice = choiceFor(t.id);
                  return (
                    <div key={t.id} className='flex items-center gap-3 px-3 py-2'>
                      <span
                        className={cn('min-w-0 flex-1', choice === 'KEEP_OPEN' && 'text-gray-400')}
                      >
                        {t.label}
                      </span>
                      <MiniCheck
                        label='Skip instead'
                        checked={choice === 'SKIPPED'}
                        disabled={choice === 'KEEP_OPEN'}
                        onToggle={() => toggle(t.id, 'SKIPPED')}
                      />
                      <MiniCheck
                        label='Keep open'
                        checked={choice === 'KEEP_OPEN'}
                        lockedReason={
                          required ? `${target?.label} can't open until this is done` : undefined
                        }
                        onToggle={() => toggle(t.id, 'KEEP_OPEN')}
                      />
                    </div>
                  );
                })}
              </Rows>
            </Section>
          )}

          {target && reopens && (
            <Section label='Reopens'>
              <Rows>
                <div className='px-3 py-2'>{target.label}</div>
              </Rows>
            </Section>
          )}

          {plan && plan.resettable.length > 0 && (
            <Section label='Steps below it'>
              <Rows>
                {plan.resettable.map((t) => {
                  const isKept = !resetTaskIds.includes(t.id);
                  return (
                    <div key={t.id} className='flex items-center gap-3 px-3 py-2'>
                      <span className='min-w-0 flex-1'>
                        <span className={cn('block', isKept && 'text-gray-400')}>{t.label}</span>
                        {/* Always one line, so checking a box never shifts the rows below. */}
                        <span className='block text-xs text-gray-500'>
                          {isKept
                            ? 'Kept as is'
                            : restarting.includes(t.id)
                              ? 'Restarts with a fresh due date'
                              : 'Back to not started'}
                        </span>
                      </span>
                      <MiniCheck
                        label='Keep as is'
                        checked={isKept}
                        lockedReason={
                          forced.includes(t.id)
                            ? `Goes back because it waits on ${waitsOn(t.dependsOn)}`
                            : undefined
                        }
                        onToggle={() => toggleKept(t.id)}
                      />
                    </div>
                  );
                })}
              </Rows>
            </Section>
          )}

          {target && isEmpty && (
            <p className='text-sm text-gray-600'>The group is already at this step.</p>
          )}

          <Section label='Reason' htmlFor='move-reason'>
            <Textarea
              className='bg-white'
              id='move-reason'
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Section>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!target || isEmpty || !reason.trim() || move.isPending}
            onClick={() =>
              target &&
              move.mutate({taskId: target.id, closes, resetTaskIds, reason: reason.trim()})
            }
          >
            {target ? `Move to ${target.label}` : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className='space-y-1.5'>
      <label
        htmlFor={htmlFor}
        className='block text-xs font-semibold uppercase tracking-wide text-gray-500'
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Rows({children}: {children: ReactNode}) {
  return (
    <div className='divide-y rounded-lg border bg-white text-sm [&>*]:min-h-10'>{children}</div>
  );
}

// lockedReason disables the box and explains why on hover.
function MiniCheck({
  label,
  checked,
  disabled,
  lockedReason,
  onToggle,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  lockedReason?: string;
  onToggle: () => void;
}) {
  const box = (
    <button
      type='button'
      role='checkbox'
      aria-checked={checked}
      disabled={disabled || Boolean(lockedReason)}
      onClick={onToggle}
      className='flex shrink-0 items-center gap-1.5 text-xs text-gray-600 disabled:opacity-40'
    >
      <span
        className={cn(
          'inline-flex h-3.5 w-3.5 items-center justify-center rounded-[3px]',
          checked ? 'bg-gray-900' : 'border border-gray-300 bg-white',
        )}
      >
        {checked && <Check className='h-2.5 w-2.5 text-white' strokeWidth={3} />}
      </span>
      {label}
    </button>
  );
  if (!lockedReason) return box;
  // Disabled buttons don't fire hover events, so the wrapper carries the tooltip.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} aria-label={lockedReason} className='cursor-help'>
          {box}
        </span>
      </TooltipTrigger>
      <TooltipContent className='max-w-60 bg-gray-900 text-white'>{lockedReason}</TooltipContent>
    </Tooltip>
  );
}
